import type { GUID } from "@pdfking/tfunc"
import type { AsyncSequence } from "@pdfking/tfunc"
import { Broadcast, ProcessIterable, UUID } from "@pdfking/tfunc"

import type { MessageRouter } from "../types/MessageRouter"
import { ReceiverAction } from "../types/ReceiverAction"
import type { ReceiverInput } from "../types/ReceiverInput"
import type { ReceiverMessage } from "../types/ReceiverMessage"
import type { ReceiverOutput } from "../types/ReceiverOutput"
import type { WorkerReceiverAPI } from "../types/WorkerReceiver"

const StreamToReceiver = <T, R>(
  stream: AsyncSequence<T>,
  { send }: WorkerReceiverAPI<T, R>,
  channel_id: GUID
): Promise<void> =>
  ProcessIterable(
    stream,
    data =>
      send({
        action: ReceiverAction.YIELD,
        channel_id,
        data,
      }),
    error =>
      send({
        action: ReceiverAction.STOP,
        channel_id,
        error,
      })
  )

async function forward_to<T, R>(
  stream: ReceiverInput<T>,
  worker: WorkerReceiverAPI<T, R>,
  channel_id: GUID,
  ...extra: ReceiverMessage<T>[]
) {
  for (const message of extra) {
    worker.send(message)
  }

  await StreamToReceiver(stream, worker, channel_id)
}

export function WorkerForward<T, R>(
  worker: WorkerReceiverAPI<T, R>,
  forward: MessageRouter<T, R>
): MessageRouter<T, R> {
  const channels: Map<GUID, ReturnType<typeof Broadcast<R | T>>> = new Map()

  const get_broadcast = (channel: GUID) => {
    let current = channels.get(channel)
    if (!current) {
      channels.set(channel, (current = Broadcast<R | T>()))
    }
    return current
  }

  worker.on((message: ReceiverMessage<R | T>) => {
    switch (message.action) {
      case ReceiverAction.START:
        const stream = get_broadcast(message.request_channel).subscribe()

        void forward_to(
          forward(stream as ReceiverInput<T>) as ReceiverInput<T>,
          worker,
          message.response_channel
        )

        break

      case ReceiverAction.YIELD:
        channels.get(message.channel_id)!.push(message.data)
        break

      case ReceiverAction.STOP:
        channels.get(message.channel_id)!.stop(message.error)
        channels.delete(message.channel_id)
        break
    }
  })

  return async function* (request: ReceiverInput<T>): ReceiverOutput<R> {
    //  new request is incoming, we forward the request to the worker and return a response.

    const request_channel = UUID()
    const response_channel = UUID()

    const stream = get_broadcast(response_channel).subscribe()

    void forward_to(request, worker, request_channel, {
      action: ReceiverAction.START,
      request_channel,
      response_channel,
    })

    yield* stream as ReceiverOutput<R>
  }
}
